import {api} from './api';
import {Photo} from '../types';
import {secureStorage} from './storage';
import {config} from '../constants';

export interface PhotosResponse {
  photos: Photo[];
  maxPhotos: number;
}

export interface PhotoProcessedEvent {
  photoId: string;
  hasThumbnail: boolean;
}

export const photoService = {
  async getSelectedPhotos(): Promise<PhotosResponse> {
    return api.get<PhotosResponse>('/api/v1/photos');
  },

  async getPhotoUrl(photoId: string): Promise<string> {
    const response = await api.get<{url: string}>(`/api/v1/photos/${photoId}/url`);
    return response.url;
  },

  async getThumbnailUrl(photoId: string): Promise<string> {
    const response = await api.get<{url: string}>(`/api/v1/photos/${photoId}/thumbnail`);
    return response.url;
  },

  async updatePhotoPosition(photoId: string, offsetX: number, offsetY: number): Promise<Photo> {
    return await api.put<Photo>(`/api/v1/photos/${photoId}/position`, {
      offsetX,
      offsetY,
    });
  },

  async deletePhoto(photoId: string): Promise<void> {
    await api.delete(`/api/v1/photos/${photoId}`);
  },

  /**
   * Subscribe to photo processing events via SSE.
   * Returns a cleanup function to close the connection.
   */
  async subscribeToProcessingEvents(
    onPhotoProcessed: (event: PhotoProcessedEvent) => void,
    onComplete: () => void,
    onError?: (error: Event) => void,
  ): Promise<() => void> {
    const token = await secureStorage.getItem(config.sessionTokenKey);
    if (!token) {
      throw new Error('No auth token available');
    }

    // Pass token as query parameter since EventSource doesn't support headers
    const url = `${config.apiBaseUrl}/api/v1/photos/processing-events?token=${encodeURIComponent(token)}`;
    const eventSource = new EventSource(url);

    eventSource.addEventListener('photo-processed', ((event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data) as PhotoProcessedEvent;
        onPhotoProcessed(data);
      } catch (e) {
        console.error('Failed to parse photo-processed event', e);
      }
    }) as EventListener);

    eventSource.addEventListener('processing-complete', (() => {
      onComplete();
      eventSource.close();
    }) as EventListener);

    eventSource.onerror = (event) => {
      console.error('SSE error', event);
      onError?.(event);
      eventSource.close();
    };

    // Return cleanup function
    return () => {
      eventSource.close();
    };
  },
};
